import { on } from '@ember/modifier'
import { ManageHeading } from 'district-ui-client/components/manage-heading'
import Panel from 'district-ui-client/components/panel'
import { RolloverAlert } from 'district-ui-client/components/rollover-alert'
import UiButton from 'district-ui-client/components/ui-button'
import type ManageStudentsIndexController from 'district-ui-client/subscription-type/manage/students/index/controller'
import eventValue from 'district-ui-client/helpers/event-value'
import themeKey from 'district-ui-client/helpers/theme-key'
import { t } from 'ember-intl'
import { FormComponentsFormSelect } from 'district-ui-client/components/form-components/form-select'
import FidgetLoadingIndicatorComponent from '@blakeelearning/fidget/components/fidget/loading-indicator'
import StudentTable from 'district-ui-client/components/student-table'
import ManageStudentActions from 'district-ui-client/components/manage-student-actions'
import UiButtonLinkTo from 'district-ui-client/components/ui-button/link-to'
import ExportStudents from 'district-ui-client/components/export-students'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { LinkTo } from '@ember/routing'
import RouteTemplate from 'ember-route-template'
import { hash } from '@ember/helper'
import { InputText } from 'district-ui-client/components/input-text'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import type FeaturesService from 'district-ui-client/services/features'
import { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import { RosterRedirectPanel } from 'district-ui-client/components/roster/redirect-panel'

interface Signature {
  Args: {
    controller: ManageStudentsIndexController
  }
}

export class ManageStudentsRouteTemplate extends Component<Signature> {
  @service externalRostering!: ExternalRosteringService

  @service features!: FeaturesService

  get showRosterRedirect() {
    return this.features.isEnabled('dui_rostering_improvements_2026') && this.externalRostering.rostersInRosterApp
  }

  get readOnly() {
    return (
      this.args.controller.is3EEnabled ||
      this.externalRostering.isEnabled ||
      this.args.controller.subscriptionType === SubscriptionType.Mathletics
    )
  }

  get disableEditingStudents() {
    return this.readOnly
  }

  get groupActionsDisabled() {
    return this.args.controller.noSelectedStudents || this.readOnly
  }

  get exportStudentsDisabled() {
    return this.externalRostering.source === 'ftp'
  }

  get showExportStudents() {
    return !this.exportStudentsDisabled
  }

  get hasSearchPromptActions() {
    return !this.disableEditingStudents || this.showExportStudents
  }

  <template>
    <ManageHeading>{{t "subscriptionType.manageStudents.index.manageStudents"}}</ManageHeading>
    {{#if this.showRosterRedirect}}
      <RosterRedirectPanel
        class="mt-6"
        @schools={{@controller.model.schoolsForSubType}}
        @destination="manage-students"
        @program={{@controller.subscriptionType}}
      />
    {{else}}
      <RolloverAlert />
      <div class="my-6 flex justify-between print:hidden">
        <div class="px-3 sm:w-full md:w-1/2 lg:w-1/3">
          <Panel
            @theme={{themeKey @controller.subscriptionType}}
            class="h-full {{if @controller.selectedStudents 'active'}}"
            @title={{t "subscriptionType.manageStudents.index.searchByStudent"}}
          >
            <form
              class="space-y-3"
              aria-label={{t "searchPlaceholder.student"}}
              {{on "submit" @controller.searchByStudent}}
            >
              <div class="flex items-center justify-between">
                <label for="student-first-name" class="w-1/3">{{t
                    "subscriptionType.manageStudents.index.firstName"
                  }}</label>
                <InputText
                  id="student-first-name"
                  class="w-2/3"
                  type="search"
                  placeholder={{t "searchPlaceholder.minimumTwoCharacters"}}
                  value={{@controller.queryFirstName}}
                  {{on "input" (eventValue @controller.setQueryFirstName)}}
                />
              </div>
              <div class="flex items-center justify-between">
                <label for="student-last-name" class="w-1/3">{{t
                    "subscriptionType.manageStudents.index.lastName"
                  }}</label>
                <InputText
                  id="student-last-name"
                  class="w-2/3"
                  type="search"
                  placeholder={{t "searchPlaceholder.minimumTwoCharacters"}}
                  value={{@controller.queryLastName}}
                  {{on "input" (eventValue @controller.setQueryLastName)}}
                />
              </div>
              <div class="text-right">
                <UiButton id="search-by-student" class="regular" type="submit" disabled={{@controller.byNameDisabled}}>
                  {{t "subscriptionType.manageStudents.index.search"}}
                </UiButton>
              </div>
            </form>
          </Panel>
        </div>
        <div class="px-3 sm:w-full md:w-1/2 lg:w-1/3">
          <Panel
            @theme={{themeKey @controller.subscriptionType}}
            class="h-full {{if @controller.selectedTeacherClassOption 'active'}}"
            @title={{t "subscriptionType.manageStudents.index.searchByTeacher"}}
          >
            <FormComponentsFormSelect
              data-test-search-by-teacher
              @defaultText="Select a teacher or class"
              @search={{true}}
              @options={{@controller.sortedTeachersOptions}}
              @searchPlaceholder="Search teachers"
              @optionClick={{@controller.selectTeacherOrSchoolClass}}
              @value={{@controller.selectedTeacherClassOption}}
            />
          </Panel>
        </div>
        <div class="px-3 sm:w-full md:w-1/2 lg:w-1/3">
          <Panel
            @theme={{themeKey @controller.subscriptionType}}
            class="h-full {{if @controller.selectedSchoolOption 'active'}}"
            @title={{t "subscriptionType.manageStudents.index.searchBySchool"}}
          >
            <FormComponentsFormSelect
              data-test-search-by-school
              @defaultText="Select a school"
              @search={{true}}
              @options={{@controller.sortedSchoolsOptions}}
              @searchPlaceholder="Search schools"
              @optionClick={{@controller.selectSchool}}
              @value={{@controller.selectedSchoolOption}}
            />
          </Panel>
        </div>
      </div>
      <div class="h-8">
        {{#if @controller.searchInFlight}}
          <FidgetLoadingIndicatorComponent
            @show={{@controller.searchInFlight}}
            @overlay={{false}}
            @centered={{true}}
            class="relative top-6"
          />
        {{/if}}
      </div>
      {{#if @controller.showResultCount}}
        <div class="my-6 text-center">
          <h2 class="m-0">
            {{t "subscriptionType.manageStudents.index.studentSearchResults"}}
            ({{@controller.productStudents.length}})
          </h2>
        </div>
      {{/if}}
      {{#if @controller.searchResult}}
        <div class="mt-6 text-center">
          <StudentTable
            @students={{@controller.productStudents}}
            @subscriptionType={{@controller.subscriptionType}}
            @isEditingDisabled={{this.disableEditingStudents}}
            @selectedIds={{@controller.selectedIds}}
            @updateSelectedIds={{@controller.updateSelectedIds}}
          >
            <ManageStudentActions
              @selectedStudents={{@controller.selectedStudents}}
              @selectedCount={{@controller.selectedIds.length}}
              @updateSelectedStudents={{@controller.updateSelectedIds}}
              @isDisabled={{this.groupActionsDisabled}}
              @schools={{@controller.sortedSchools}}
            />
            <div class="mx-3 mb-3 inline-flex align-middle">
              <UiButtonLinkTo
                @route={{@controller.addStudentRouteName}}
                @query={{hash schoolId=@controller.schoolId}}
                class="muted"
                @corners="rounded rounded-r-none"
                @disabled={{this.disableEditingStudents}}
                data-test-new-student-link
              >
                <PhosphorIcon @icon="phosphor-user-fill" class="mr-0.5" />
                {{t "subscriptionType.manageStudents.index.addStudent"}}
              </UiButtonLinkTo>
              <UiButtonLinkTo
                @route={{@controller.importStudentsRouteName}}
                class="muted"
                @corners="rounded rounded-r-none"
                @disabled={{this.disableEditingStudents}}
                data-test-import-students-link
              >
                <PhosphorIcon @icon="phosphor-users-three-fill" class="mr-0.5" />
                {{t "importStudents"}}
              </UiButtonLinkTo>

              <ExportStudents
                @school={{@controller.currentSchoolForExport}}
                @showAsButton={{true}}
                @disableButton={{this.exportStudentsDisabled}}
              />
            </div>
          </StudentTable>
        </div>
      {{else}}
        <div class="mt-6 text-center">
          <PhosphorIcon @icon="phosphor-magnifying-glass-fill" />
          {{#if this.hasSearchPromptActions}}
            {{t "performSearch"}}
          {{else}}
            {{t "performSearchNoActions"}}
          {{/if}}
          <div class="space-x-3">
            {{#unless this.disableEditingStudents}}
              <LinkTo
                @route={{@controller.addStudentRouteName}}
                @model={{@controller.subscriptionType}}
                class="text-oceany-blue-300 hover:text-oceany-blue-350 focus:text-oceany-blue-350"
              >
                <PhosphorIcon @icon="phosphor-user-fill" class="mr-0.5 text-black" />
                {{t "subscriptionType.manageStudents.index.addStudent"}}
              </LinkTo>
              <LinkTo
                @route={{@controller.importStudentsRouteName}}
                @model={{@controller.subscriptionType}}
                class="text-oceany-blue-300 hover:text-oceany-blue-350 focus:text-oceany-blue-350"
              >
                <PhosphorIcon @icon="phosphor-users-three-fill" class="mr-0.5 text-black" />
                {{t "importStudents"}}
              </LinkTo>
            {{/unless}}
            {{#if this.showExportStudents}}
              <ExportStudents @school={{@controller.currentSchoolForExport}} />
            {{/if}}
          </div>
        </div>
      {{/if}}
    {{/if}}
  </template>
}
export default RouteTemplate(ManageStudentsRouteTemplate)
